import { useCallback, useEffect, useReducer } from 'react';

import { isProcessingPaymentStatus } from '../constants/payment-methods';
import { useFeatureFlags } from '../contexts/feature-flags-context';
import { usePaymentErrorContext } from '../contexts/payment-error-context';
import { usePaymentState } from '../contexts/payment-state-context';
import { PaymentError } from '../core/payment-error';
import { PaymentProcessor } from '../core/payment-processor';
import { PaymentMethod } from '../types';
import { isDirectPaymentMethod } from '../utils/payment-methods';
import { useSavedPaymentMethods } from './use-saved-payment-methods';

type PaymentManagerState =
    | { view: 'loading' }
    | {
          view: 'main';
          selectedMethod: string | null;
          processingMethodId: string | null;
          isLoadingAddNew: boolean;
      }
    | { view: 'selecting-type'; loadingMethodType: PaymentMethod | null }
    | { view: 'adding-method'; methodType: PaymentMethod };

type PaymentManagerEvent =
    | { type: 'START_ADD_NEW' }
    | { type: 'START_LOADING_ADD_NEW' }
    | { type: 'STOP_LOADING_ADD_NEW' }
    | { type: 'START_LOADING_TYPE_SELECT'; methodType: PaymentMethod }
    | { type: 'STOP_LOADING_TYPE_SELECT' }
    | { type: 'SELECT_PAYMENT_TYPE'; methodType: PaymentMethod }
    | { type: 'CANCEL_ADD_NEW' }
    | { type: 'CANCEL_TO_TYPE_SELECTION' }
    | { type: 'CANCEL_TO_MAIN' }
    | { type: 'PAYMENT_METHOD_ADDED' }
    | { type: 'SELECT_SAVED_METHOD'; methodId: string }
    | { type: 'START_PAYMENT_PROCESSING'; methodId: string }
    | { type: 'PAYMENT_SUCCESS'; methodId: string }
    | { type: 'PAYMENT_FAILED'; methodId: string };

const paymentManagerReducer = (
    state: PaymentManagerState,
    event: PaymentManagerEvent
): PaymentManagerState => {
    switch (event.type) {
        case 'START_ADD_NEW':
            return { view: 'selecting-type', loadingMethodType: null };

        case 'START_LOADING_ADD_NEW':
            return state.view === 'main'
                ? { ...state, isLoadingAddNew: true }
                : state;

        case 'STOP_LOADING_ADD_NEW':
            return state.view === 'main'
                ? { ...state, isLoadingAddNew: false }
                : state;

        case 'START_LOADING_TYPE_SELECT':
            return state.view === 'selecting-type'
                ? { ...state, loadingMethodType: event.methodType }
                : state;

        case 'STOP_LOADING_TYPE_SELECT':
            return state.view === 'selecting-type'
                ? { ...state, loadingMethodType: null }
                : state;

        case 'SELECT_PAYMENT_TYPE':
            return { view: 'adding-method', methodType: event.methodType };

        case 'CANCEL_ADD_NEW':
            return {
                view: 'main',
                selectedMethod: null,
                processingMethodId: null,
                isLoadingAddNew: false
            };

        case 'CANCEL_TO_TYPE_SELECTION':
            return { view: 'selecting-type', loadingMethodType: null };

        case 'CANCEL_TO_MAIN':
            return {
                view: 'main',
                selectedMethod: null,
                processingMethodId: null,
                isLoadingAddNew: false
            };

        case 'PAYMENT_METHOD_ADDED':
            return {
                view: 'main',
                selectedMethod: null,
                processingMethodId: null,
                isLoadingAddNew: false
            };

        case 'SELECT_SAVED_METHOD':
            return {
                view: 'main',
                selectedMethod: event.methodId,
                processingMethodId: null,
                isLoadingAddNew: false
            };

        case 'START_PAYMENT_PROCESSING':
            return state.view === 'main'
                ? { ...state, processingMethodId: event.methodId }
                : state;

        case 'PAYMENT_SUCCESS':
            return state.view === 'main'
                ? {
                      ...state,
                      selectedMethod: event.methodId,
                      processingMethodId: null
                  }
                : state;

        case 'PAYMENT_FAILED':
            return state.view === 'main'
                ? { ...state, selectedMethod: null, processingMethodId: null }
                : state;

        default:
            return state;
    }
};

export const usePaymentMethodManager = (paymentProcessor: PaymentProcessor) => {
    const [state, dispatch] = useReducer(paymentManagerReducer, {
        view: 'main',
        selectedMethod: null,
        processingMethodId: null,
        isLoadingAddNew: false
    });

    const { addError, clearErrors } = usePaymentErrorContext();
    const { setProcessing, setPaymentComplete } = usePaymentState();
    const { flags } = useFeatureFlags();

    const {
        methods: savedMethods,
        isLoading,
        isLoaded,
        selectedMethodId,
        refetch
    } = useSavedPaymentMethods(paymentProcessor);

    useEffect(() => {
        if (isLoaded && selectedMethodId && state.view === 'main') {
            dispatch({
                type: 'SELECT_SAVED_METHOD',
                methodId: selectedMethodId
            });
        }
    }, [isLoaded, selectedMethodId, state.view]);

    useEffect(() => {
        const isProcessing =
            (state.view === 'main' && state.processingMethodId !== null) ||
            (state.view === 'selecting-type' &&
                state.loadingMethodType !== null);
        setProcessing(isProcessing);
    }, [state, setProcessing]);

    useEffect(() => {
        const isComplete =
            state.view === 'main' &&
            state.selectedMethod !== null &&
            state.processingMethodId === null;
        setPaymentComplete(isComplete);
    }, [state, setPaymentComplete]);

    const handlePaymentMethodAdded = useCallback(async () => {
        paymentProcessor.clearPaymentRequestToken();

        if (flags.showPaymentInstrumentList ?? true) {
            dispatch({
                type: 'PAYMENT_METHOD_ADDED'
            });
            await refetch();
        }
    }, [refetch, paymentProcessor, flags]);

    const handleDirectPaymentSuccess = useCallback(async () => {
        dispatch({ type: 'STOP_LOADING_TYPE_SELECT' });

        if (flags.showPaymentInstrumentList ?? true) {
            await refetch();
            dispatch({
                type: 'PAYMENT_METHOD_ADDED'
            });
        }
    }, [refetch, flags]);

    useEffect(() => {
        paymentProcessor.setGlobalErrorHandler((error) => {
            addError(error);
        });
    }, [paymentProcessor, addError]);

    const handleEvent = useCallback((event: PaymentManagerEvent) => {
        dispatch(event);
    }, []);

    const handleDirectPaymentFlow = useCallback(
        async (methodType: PaymentMethod) => {
            await paymentProcessor.createPaymentRequest();

            const existingPayment = await paymentProcessor.getPayment();

            if (
                !existingPayment?.status ||
                !isProcessingPaymentStatus(existingPayment.status)
            ) {
                await paymentProcessor.initiatePayment({
                    paymentMethod: methodType
                });
            }

            await paymentProcessor.processPayment(
                { paymentMethodDetails: '{}' },
                {},
                {
                    onSucceeded: () => {
                        void handleDirectPaymentSuccess();
                    },
                    onError: (error) => {
                        dispatch({ type: 'STOP_LOADING_TYPE_SELECT' });
                        addError(error);
                    }
                }
            );
        },
        [paymentProcessor, addError, handleDirectPaymentSuccess]
    );

    const handleTypeSelect = useCallback(
        async (methodType: PaymentMethod): Promise<boolean> => {
            try {
                clearErrors();
                dispatch({ type: 'START_LOADING_TYPE_SELECT', methodType });

                if (!isDirectPaymentMethod(methodType)) {
                    await paymentProcessor.createPaymentRequest();
                    dispatch({ type: 'SELECT_PAYMENT_TYPE', methodType });
                    return true;
                }

                await handleDirectPaymentFlow(methodType);
                return true;
            } catch (error) {
                dispatch({ type: 'STOP_LOADING_TYPE_SELECT' });
                addError(error);
                return false;
            }
        },
        [addError, clearErrors, paymentProcessor, handleDirectPaymentFlow]
    );

    const handleMethodSelect = useCallback(
        async (methodId: string) => {
            clearErrors();

            if (methodId === 'add-new') {
                try {
                    dispatch({ type: 'START_LOADING_ADD_NEW' });
                    dispatch({ type: 'START_ADD_NEW' });
                } catch (error) {
                    dispatch({ type: 'STOP_LOADING_ADD_NEW' });
                    addError(error);
                }
                return;
            }

            dispatch({ type: 'START_PAYMENT_PROCESSING', methodId });

            const selectedPaymentMethod = savedMethods.find(
                (m) => m.id === methodId
            );
            if (selectedPaymentMethod) {
                const onError = (error: PaymentError) => {
                    dispatch({ type: 'PAYMENT_FAILED', methodId });
                    addError(error);
                };

                try {
                    if (selectedPaymentMethod.paymentRequestToken) {
                        paymentProcessor.setPaymentRequestToken(
                            selectedPaymentMethod.paymentRequestToken
                        );

                        await paymentProcessor.processPayment(undefined, {}, {
                            onSucceeded: () => {
                                dispatch({ type: 'PAYMENT_SUCCESS', methodId });
                            },
                            onError
                        });
                    } else {
                        await paymentProcessor.createPaymentRequest();
                        await paymentProcessor.initiatePayment({
                            paymentMethod: selectedPaymentMethod.paymentMethod,
                            paymentInstrumentToken: selectedPaymentMethod.id
                        });

                        await paymentProcessor.processPayment(undefined, {}, {
                            onSucceeded: () => {
                                dispatch({ type: 'PAYMENT_SUCCESS', methodId });
                                void refetch();
                            },
                            onError
                        });
                    }
                } catch (error) {
                    dispatch({ type: 'PAYMENT_FAILED', methodId });
                    addError(error);
                }
            } else {
                dispatch({ type: 'PAYMENT_FAILED', methodId });
            }
        },
        [savedMethods, paymentProcessor, addError, clearErrors, refetch]
    );

    return {
        state,
        savedMethods,
        isLoading,
        isLoaded,

        handleEvent,
        handleMethodSelect,
        handleTypeSelect,
        handlePaymentMethodAdded,

        get currentView() {
            return state.view;
        },
        get selectedMethod() {
            return state.view === 'main' ? state.selectedMethod : null;
        },
        get currentMethodType() {
            return state.view === 'adding-method' ? state.methodType : null;
        },
        get processingMethodId() {
            return state.view === 'main' ? state.processingMethodId : null;
        },
        get isPaymentLoading() {
            return state.view === 'main' && state.processingMethodId !== null;
        },
        get isLoadingAddNew() {
            return state.view === 'main' ? state.isLoadingAddNew : false;
        },
        get loadingMethodType() {
            return state.view === 'selecting-type'
                ? state.loadingMethodType
                : null;
        }
    };
};
